// Session Z82 — Guest-side dark-mode hook with signed-cookie persistence.
//
// Why a bespoke hook (rather than reusing Layout's `useDarkMode`)?
// The staff CRM only needs localStorage because operators log in and we can
// stamp preferences against their user record. Guests don't log in — their
// magic link may land them on a device-less browser session. Signing the
// cookie with the server-side JWT_SECRET means a tampered value falls back
// to "auto" instead of silently applying.
//
// Flow:
//   1. On mount: GET /api/public/guest/dark-mode (sends the cookie).
//   2. If the server returns a valid mode, apply it immediately.
//   3. On every change: POST the new mode so the signed cookie refreshes.
//   4. On "auto": watch `prefers-color-scheme: dark` and re-apply live.

import { useEffect, useState } from "react";
import axios from "axios";
import { Moon, Sun, MonitorSmartphone } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function readCookieMode() {
  // The browser can only see the mode half of `ns_guest_dark=mode.sig` —
  // we treat this strictly as a "paint-before-fetch" hint, verified below by
  // the backend's HMAC check on the next GET.
  try {
    const row = document.cookie.split("; ").find((c) => c.startsWith("ns_guest_dark="));
    if (!row) return null;
    const raw = decodeURIComponent(row.split("=")[1] || "");
    const mode = raw.split(".")[0];
    return ["auto", "light", "dark"].includes(mode) ? mode : null;
  } catch { return null; }
}

export function useGuestDarkMode() {
  const [mode, setMode] = useState(() => readCookieMode() || "auto");
  const [hydrated, setHydrated] = useState(false);

  // 1) Verify against the server (HMAC check) on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BASE}/api/public/guest/dark-mode`, { withCredentials: true });
        if (cancelled) return;
        const verified = r.data?.mode;
        if (verified) setMode(verified);
      } catch { /* offline / blocked — keep the cookie hint */ }
      if (!cancelled) setHydrated(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // 2) Apply + persist.
  useEffect(() => {
    const root = document.documentElement;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = mode === "dark" || (mode === "auto" && mql.matches);
      root.classList.toggle("dark", dark);
    };
    apply();
    if (hydrated) {
      axios.post(
        `${BASE}/api/public/guest/dark-mode`,
        { mode },
        { withCredentials: true },
      ).catch(() => {});
    }
    if (mode === "auto") {
      mql.addEventListener("change", apply);
      return () => mql.removeEventListener("change", apply);
    }
    return undefined;
  }, [mode, hydrated]);

  return [mode, setMode];
}


export function GuestDarkToggle({ className = "" }) {
  const [mode, setMode] = useGuestDarkMode();
  const next = mode === "auto" ? "light" : mode === "light" ? "dark" : "auto";
  const Icon = mode === "dark" ? Sun : mode === "light" ? Moon : MonitorSmartphone;
  const label =
    mode === "auto"
      ? "Auto — follows your device"
      : mode === "light"
        ? "Light mode"
        : "Dark mode";
  return (
    <button
      type="button"
      data-testid="guest-darkmode-toggle"
      data-mode={mode}
      onClick={() => setMode(next)}
      title={`${label} · click to switch to ${next}`}
      aria-label={label}
      className={`rounded-full h-11 w-11 inline-flex items-center justify-center border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 dark:bg-slate-900/40 dark:border-slate-700 dark:text-slate-200 ${className}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
